import apiErrors from '../../../utils/ApiCloudErrors/apiErrors.json';

export type MessageStatus =
  | 'buffering'
  | 'sending'
  | 'error'
  | 'read'
  | 'delivered'
  | 'sent';

const TICK_SOURCES = new Set(['whatsapp', 'messenger', 'messenger-fb', 'instagram-dm']);
const NO_TICK_SOURCES = new Set(['web']);
const OUTBOUND_SENDERS = new Set(['bot', 'human']);

export interface MessageStatusInput {
  sent_at?: string | null;
  delivered_at?: string | null;
  read_at?: string | null;
  failed_at?: string | null;
  error_code?: number | string | null;
  error_message?: string | null;
  source_id?: string | null;
  sender?: string | null;
  // optimistic-only client flag
  localStatus?: 'buffering' | 'sending' | 'error' | null;
  // attachments only — never used for ticks
  metadata?: {
    [k: string]: any;
  } | null;
}

/**
 * Receipts are for outbound bot/human messages on channels with real
 * delivery/read tracking. Web is a sync chat with no client ACK, so ticks
 * stay hidden even if receipt columns are written.
 */
export function shouldShowMessageTicks(
  sender?: string | null,
  source?: string | null,
): boolean {
  const normalizedSender = (sender || '').toLowerCase();
  const normalizedSource = (source || '').toLowerCase();
  if (NO_TICK_SOURCES.has(normalizedSource)) return false;
  return OUTBOUND_SENDERS.has(normalizedSender) && TICK_SOURCES.has(normalizedSource);
}

/**
 * Derives a single most-advanced status from dedicated columns.
 * Order: localStatus → failed_at → read_at → delivered_at → sent_at.
 * Missing receipts default to sent (same as the old metadata path).
 * Does not read metadata for ticks.
 */
export function deriveMessageStatus(m: MessageStatusInput): MessageStatus {
  if (!m) return 'sent';

  if (m.localStatus === 'buffering') return 'buffering';
  if (m.localStatus === 'sending') return 'sending';
  if (m.localStatus === 'error') return 'error';

  if (m.failed_at) return 'error';
  if (m.read_at) return 'read';
  if (m.delivered_at) return 'delivered';
  if (m.sent_at) return 'sent';

  return 'sent';
}

/**
 * Resolves the ISO timestamp for a receipt column. Null means that step
 * has not happened yet — not "unknown".
 */
export function getStatusTimestamp(
  m: MessageStatusInput,
  field: 'sent' | 'delivered' | 'read' | 'failed',
): { value: string; type: 'iso' } | null {
  const isoMap = {
    sent: m?.sent_at,
    delivered: m?.delivered_at,
    read: m?.read_at,
    failed: m?.failed_at,
  } as const;

  const iso = isoMap[field];
  if (iso) return { value: iso, type: 'iso' };

  return null;
}

/** Known backend / Meta error texts → Meta error codes (for MessageErrorIndicator). */
const ERROR_TEXT_TO_CODE: Record<string, number> = {
  'business eligibility payment issue': 131042,
  'whatsapp provided number needs display name approval before message can be sent.': 131037,
  'message undeliverable': 131026,
  'service unavailable': 131016,
  'spam rate limit hit': 131048,
  'this message was not delivered to maintain healthy ecosystem engagement.': 131049,
  'media upload error': 131053,
  // send-message: conversation / 24h window expired
  'the conversation window has expired for this platform; you can only send approved templates': 131047,
};

/**
 * Maps a known error message string to a Meta error code, if any.
 */
export function resolveErrorCodeFromText(text: string | null | undefined): number | null {
  if (!text || typeof text !== 'string') return null;
  return ERROR_TEXT_TO_CODE[text.toLowerCase().trim()] ?? null;
}

function parseStrictErrorCode(value: number | string): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const num = Number(trimmed);
  return Number.isFinite(num) ? num : null;
}

/** Resolves a Meta/WhatsApp error code from `error_code` / `error_message`. */
export function resolveErrorCode(m: MessageStatusInput): number | null {
  if (m?.error_code != null) {
    const num = parseStrictErrorCode(m.error_code);
    if (num != null) return num;
  }

  return resolveErrorCodeFromText(m?.error_message);
}

/**
 * Returns the error text from the `error_message` column.
 */
export function resolveErrorMessage(m: MessageStatusInput): string | null {
  if (m?.error_message) return m.error_message;
  return null;
}

const errorReferenceByCode: Record<number, string> = {
  131042: 'https://www.facebook.com/business/help/2225184664363779?id=2129163877102343',
  131037: 'https://www.facebook.com/business/help/378834799515077',
  131026: '',
  131016: 'https://metastatus.com/whatsapp-business-api',
  131047: 'https://developers.facebook.com/docs/whatsapp/cloud-api/guides/send-messages#customer-service-windows',
  131048: 'https://developers.facebook.com/docs/whatsapp/messaging-limits#quality-rating-and-messaging-limits',
  131049: 'https://developers.facebook.com/docs/whatsapp/cloud-api/guides/send-message-templates#per-user-marketing-template-message-limits',
  131053: 'https://developers.facebook.com/docs/whatsapp/cloud-api/reference/media#supported-media-types',
};

export interface ResolvedErrorDetails {
  code: number | null;
  message: string | null;
  descriptionKey: string | null;
  solutionKey: string | null;
  reference: string;
}

/**
 * Returns structured error metadata suitable for the error info dialog.
 */
export function getErrorDetails(m: MessageStatusInput): ResolvedErrorDetails {
  const code = resolveErrorCode(m);
  const message = resolveErrorMessage(m);

  if (code == null) {
    return {
      code: null,
      message,
      descriptionKey: null,
      solutionKey: null,
      reference: '',
    };
  }

  const known = (apiErrors as any[]).find((err) => err.error_code === code);
  return {
    code,
    message,
    descriptionKey: known ? `meta_errors.${code}.description` : null,
    solutionKey: known ? `meta_errors.${code}.possible_solution` : null,
    reference: errorReferenceByCode[code] || '',
  };
}

/**
 * True when the row has a failed receipt or error columns to surface.
 */
export function hasMessageError(m: MessageStatusInput): boolean {
  if (!m) return false;
  if (m.failed_at) return true;
  if (m.error_code != null) return true;
  if (m.error_message) return true;
  if (m.localStatus === 'error') return true;
  return false;
}

/**
 * Truthy when delivered_at is set on the row.
 */
export function isDelivered(m: MessageStatusInput): boolean {
  return Boolean(m?.delivered_at);
}

/** Red "error" caption: failed, and not delivered to any device. */
export function shouldShowErrorCaption(m: MessageStatusInput): boolean {
  return hasMessageError(m) && !isDelivered(m);
}

export interface ChatLastMessagePreview {
  last_msg_sender?: string | null;
  last_message_sent_at?: string | null;
  last_message_delivered_at?: string | null;
  last_message_read_at?: string | null;
  last_message_failed_at?: string | null;
  last_message_error_code?: number | string | null;
  last_message_error_message?: string | null;
}

/** Persist receipt columns from a Hasura last_message onto a chat list row. */
export function lastMessageReceiptFields(lastMessage?: any) {
  const row = lastMessage || {};
  return {
    last_message_sent_at: row.sent_at ?? null,
    last_message_delivered_at: row.delivered_at ?? null,
    last_message_read_at: row.read_at ?? null,
    last_message_failed_at: row.failed_at ?? null,
    last_message_error_code: row.error_code ?? null,
    last_message_error_message: row.error_message ?? null,
  };
}

/** Rebuild the tick input from a chat-list preview row. */
export function messageStatusFromChatPreview(
  chat: ChatLastMessagePreview,
): MessageStatusInput {
  return {
    sender: chat.last_msg_sender,
    sent_at: chat.last_message_sent_at,
    delivered_at: chat.last_message_delivered_at,
    read_at: chat.last_message_read_at,
    failed_at: chat.last_message_failed_at,
    error_code: chat.last_message_error_code,
    error_message: chat.last_message_error_message,
  };
}
